import { Box, Stack, Typography, useTheme } from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { ReactElement } from "react"
import { useNavigate } from "react-router-dom"

import { ReactComponent as CarSvg } from "../../assets/kpi-three.svg"
import {
    DB_ASSESSOR_DEMAND,
    DB_ASSESSOR_GEN,
    DISPLAY_ROLES,
    DNO_APPROVED,
    ENA_SYS_ADMIN,
    INSTALLER,
    LCT_MANU_APPROVED,
} from "../../constants/roles"
import { useScreenSize } from "../../hooks/useScreenSize"
import {
    ADMIN_PORTAL,
    DNO_APPLICATIONS_ESCALATIONS,
    GEN_DEVICE_SEARCH,
    NEW_SINGLE_APPLICATION,
    PUBLISH_NEW_LCT_TTR_DEVICE,
} from "../../router/routes"
import { GRID_SPACING } from "../../themes/defaultThemeParams"
import {
    GEN_DEVICE_TO_ASSESS,
    HP_TO_ASSESS,
} from "../../utils/routes/routeUtils"
import AppButton from "../buttons/appButton"
import DisplayIf from "../wrappers/displayIf"

type IDashboardPanelFrameProps = {
    userName: string
    permission: string
    additionalPermission?: boolean
}

/**
 * Header layout for the dashboard panel
 */
const DashboardPanelFrame = ({
    userName,
    permission,
    additionalPermission,
}: IDashboardPanelFrameProps): ReactElement => {
    const theme = useTheme()
    const navigate = useNavigate()
    const { isTablet, isMob } = useScreenSize()

    const permissionToUrlMap = {
        [DISPLAY_ROLES[DNO_APPROVED]]: additionalPermission
            ? DNO_APPLICATIONS_ESCALATIONS
            : GEN_DEVICE_SEARCH,
        [DISPLAY_ROLES[INSTALLER]]: NEW_SINGLE_APPLICATION,
        [DISPLAY_ROLES[LCT_MANU_APPROVED]]: PUBLISH_NEW_LCT_TTR_DEVICE,
        [DISPLAY_ROLES[ENA_SYS_ADMIN]]: ADMIN_PORTAL,
        [DISPLAY_ROLES[DB_ASSESSOR_DEMAND]]: HP_TO_ASSESS,
        [DISPLAY_ROLES[DB_ASSESSOR_GEN]]: GEN_DEVICE_TO_ASSESS,
    }

    const handleUrl = (): void => {
        const url = permissionToUrlMap[permission]
        if (url) {
            navigate(url)
        }
    }

    const isNotDnoReviewer =
        additionalPermission === false && permission === "DNO"

    return (
        <Grid xs={12}>
            <Stack
                direction="row"
                spacing={GRID_SPACING}
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    paddingX: { xs: theme.spacing(0), sm: theme.spacing(2) },
                    paddingY: theme.spacing(0),
                }}
            >
                <Typography
                    variant="h4"
                    fontWeight={500}
                    justifyContent="center"
                    color={theme.palette.text.primary}
                >
                    Welcome Back{" "}
                    <Box color={theme.palette.primary.main} display="inline">
                        {userName}!
                    </Box>{" "}
                    <Box display="block">
                        <Typography
                            color={theme.palette.text.secondary}
                            variant="h6"
                            fontWeight={300}
                            marginBottom={1}
                        >
                            You are signed in as an Approved {permission}
                        </Typography>
                        <DisplayIf condition={isNotDnoReviewer}>
                            <Typography
                                color={theme.palette.text.secondary}
                                variant="h6"
                                fontWeight={300}
                                marginBottom={1}
                            >
                                To become a DNO reviewer ask your admin to grant
                                you the permission
                            </Typography>
                        </DisplayIf>
                        <DisplayIf condition={!isNotDnoReviewer}>
                            <AppButton variant="contained" onClick={handleUrl}>
                                Let&apos;s Get Started
                            </AppButton>
                        </DisplayIf>
                    </Box>
                </Typography>
                <DisplayIf condition={!isMob}>
                    <CarSvg
                        width={isTablet ? "200px" : "400px"}
                        height={isTablet ? "90px" : "180px"}
                        viewBox="800 320 650 650"
                    />
                </DisplayIf>
            </Stack>
        </Grid>
    )
}

DashboardPanelFrame.defaultProps = {
    additionalPermission: false,
}

export default DashboardPanelFrame
